import { useEffect, useState } from 'react';

import { Container } from '@/components/Container';
import type {
  ContentfulHeadingFragment,
  ContentfulPlaceholderStoresFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown, useIsXlDown } from '@/hooks/useMedia';
import type { StoreHit } from '@/integrations/algolia/types';

import { BoutiqueCard } from '../BoutiqueCard';
import { BoutiqueSlider } from '../BoutiqueSlider';
import { Heading } from '../Cms/Heading/Heading';
import { FindAStore } from '../FindAStore';

import * as S from './BoutiqueList.styles';

export const BoutiquesList = ({
  info,
  stores,
}: {
  info: ContentfulPlaceholderStoresFragment;
  stores: StoreHit[];
}) => {
  const findAStoreContent = info.findAStore;
  const storesHits = stores;
  return (
    <Container
      data-test-id='BoutiquesListContainer'
      disableGutters
      maxWidth='lg'
    >
      {info.placeholderHeading && (
        <Heading
          Heading={info.placeholderHeading as ContentfulHeadingFragment}
          data-test-id='BoutiquesListHeading'
          shouldApplyBottomPadding
        ></Heading>
      )}
      {stores.length > 0 && (
        <BoutiqueSlider
          data-test-id='BoutiquesListBoutiqueSlider'
          disableGutters
          variantColor='dark'
        >
          {findAStoreContent && (
            <StoreListHasFindAStore info={info} stores={storesHits} />
          )}
          {!findAStoreContent && <StoreList info={info} stores={storesHits} />}
        </BoutiqueSlider>
      )}
      {stores.length == 0 && (
        <FindAStore
          cardType='onlyBanner'
          data-test-id='BoutiquesListFindAStore'
          info={info}
          noStoreResults={true}
        ></FindAStore>
      )}
    </Container>
  );
};

const StoreListHasFindAStore = ({
  info,
  stores,
}: {
  info: ContentfulPlaceholderStoresFragment;
  stores: StoreHit[];
}) => {
  const [isMobileView, setIsMobileView] = useState(false);
  const isXlDown = useIsXlDown();
  const isMdDown = useIsMdDown();
  useEffect(() => {
    if (info.numberOfStores === 3) {
      setIsMobileView(isXlDown);
    } else {
      setIsMobileView(isMdDown);
    }
  }, [info.numberOfStores]);

  const findAStore = info.findAStore;
  const slides = 4;
  return (
    <BoutiqueSlider.Slider
      data-test-id='BoutiquesListStoreListHasFindAStoreSlider'
      isMobileView={isMobileView}
      slidesPerView={slides}
      spaceBetween={16}
    >
      {stores.map((item, i) => {
        return (
          <S.BoutiqueCardStyled
            key={i}
            Boutique={item}
            data-test-id={`BoutiquesListStoreListHasFindAStoreBoutiqueCard-${i}`}
            index={i}
            isMobileView={isMobileView}
            placeholderStoresColor={
              info.placeholderStoresColor ? info.placeholderStoresColor : ''
            }
            type={info.type as string}
          ></S.BoutiqueCardStyled>
        );
      })}
      {findAStore && (
        <FindAStore
          cardType='card'
          data-test-id='BoutiquesListStoreListHasFindAStoreFindAStore'
          info={info}
        ></FindAStore>
      )}
    </BoutiqueSlider.Slider>
  );
};

export const StoreList = ({
  info,
  stores,
}: {
  info: ContentfulPlaceholderStoresFragment;
  stores: StoreHit[];
}) => {
  const isMobileView = useIsMdDown();
  const slides = 4;
  return (
    <BoutiqueSlider.Slider
      data-test-id='BoutiquesListStoreListSlider'
      isMobileView={isMobileView}
      slidesPerView={isMobileView ? undefined : slides}
      spaceBetween={20}
    >
      {stores.map((item, i) => {
        return (
          <BoutiqueCard
            key={i}
            Boutique={item}
            data-test-id={`BoutiquesListStoreListBoutiqueCard-${i}`}
            index={i}
            isMobileView={isMobileView}
            placeholderStoresColor={
              info.placeholderStoresColor ? info.placeholderStoresColor : ''
            }
            type={info.type as string}
          ></BoutiqueCard>
        );
      })}
    </BoutiqueSlider.Slider>
  );
};

BoutiquesList.displayName = 'BoutiquesList';
